/*
  component: post-item
  title: Post Item Web Component
  props:
    - postData: set the post data
  methods:
    - setCurrentUser: set the current user
  deps:
    - washing-machine-icon
    - photocopy-icon
    - comments-section
    - markdown-preview
    - music-player
*/
import { applyImageGating, applyImageModeration } from '../utils/image-gating.js';
import {
  attachProxyImageRemintHandlers,
  processImageProxyUrls
} from '../utils/image-proxy-processor.js';
import { escapeHtml } from '../utils/html-utils.js';
import { openGoogleDrivePicker } from './google-drive-picker.js';
import { buildGroupPostUrl } from '../utils/group-permalink.js';
class PostItem extends HTMLElement {
  constructor() {
    super();
    this._post = null;
    this._currentUser = null;
    this._keepCommentsOpen = false;
  }

  static get observedAttributes() {
    return ['post-data', 'group-id'];
  }

  connectedCallback() {
    // Check if post-data attribute is already set (it might be set before connection)
    const postDataAttr = this.getAttribute('post-data');
    if (postDataAttr && !this._post) {
      try {
        this._post = JSON.parse(postDataAttr);
        window.logger?.component('post-item connected with post data', this._post?.id);
      } catch (error) {
        window.logger?.error('Invalid post data in connectedCallback:', error, postDataAttr?.substring(0, 100));
      }
    } else if (!postDataAttr) {
      window.logger?.component('post-item connected without post-data attribute');
    }

    this.render().catch(err => {
      window.logger?.error('Error rendering post-item:', err);
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'post-data' && newValue) {
      try {
        this._post = JSON.parse(newValue);
        this.render().catch(err => {
          window.logger?.error('Error rendering post-item:', { err });
        });
      } catch (error) {
        window.logger.error('Invalid post data:', error);
      }
    }
  }

  setCurrentUser(data) {
    this._currentUser = data;
  }

  set postData(data) {
    this._post = data;
    this.render().catch(err => {
      window.logger?.error('Error rendering post-item:', { err });
    });

    // Set comment count for lazy loading
    const commentsSection = this.querySelector('comments-section');
    if (commentsSection && data.comment_count !== undefined) {
      commentsSection.setCurrentUser(this._currentUser);
      commentsSection.setCommentCount(data.comment_count);
    }
  }

  set expandComments(data) {
    if (!data) {
      return;
    }
    if (this.querySelector('.post-comments')) {
      this.openComments();
    } else {
      this._expandCommentsWhenReady = true;
    }
  }

  get postData() {
    return this._post;
  }

  get groupId() {
    const raw = this.getAttribute('group-id');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  }

  isGroupPost() {
    return this.groupId !== null;
  }

  async render() {
    if (!this._post) {
      this.innerHTML = '<div class="loading">Loading post...</div>';
      return;
    }

    const {
      id, title, content_html, username, created_at, is_owner, prompt_title, prompt_body: _promptBody,
      prompt_template_type, prompt_template_structure,
      post_type, mood_icon, mood_status, music_url: _musicUrl, resolved_url, embed_platform, embed_id,
      embed_url, embed_content_type, embed_country, url_title, url_description, url_image,
      import_source_site, import_source_url, source, group_name: groupName,
      url_site_name, rss_feed_id, notify_friends_email
    } = this._post;
    const imported = source === 'rss' || source === 'facebook';
    const import_source = source;
    const isRssPost = import_source === 'rss';
    const rssBlogName = url_site_name || import_source_site || 'RSS';
    const createdDate = new Date(created_at);
    const timeAgo = this.formatTimeAgo(createdDate);
    const inGroupShell = this.closest('group-shell') !== null;
    const showGroupBadge = this.isGroupPost() && groupName && !inGroupShell;
    const groupIdAttr = this.isGroupPost() ? ` group-id="${this.groupId}"` : '';
    const permalink = this.isGroupPost()
      ? (inGroupShell ? `#post-${id}` : buildGroupPostUrl({ groupId: this.groupId, postId: id }))
      : `/post/${id}`;

    const isMemoryShare = source === 'memory_share';
    const memoryClass = isMemoryShare ? ' memory-share' : '';
    const postAnchorId = inGroupShell ? ` id="post-${id}"` : '';
    const commentsOpen = this._keepCommentsOpen || this.getAttribute('data-comments-open') === 'true';
    const commentsDisplayStyle = commentsOpen ? '' : ' style="display: none;"';
    const commentsToggleLabel = commentsOpen
      ? '💬 <span class="comments-count">Hide Comments</span>'
      : `💬 <span class="comments-count" id="comments-count-${id}">Comments (${this._post.comment_count || 0})</span>`;

    const styleClasses = (this._post.style_classes || []).join(' ');
    const styleInline = this._post.style_inline || '';
    const styledClass = styleClasses ? ` ${styleClasses}` : '';
    const styleAttr = styleInline ? ` style="${styleInline}"` : '';

    this.innerHTML = `
      <article class="card post-item${memoryClass}${styledClass}" data-post-id="${id}"${postAnchorId}${styleAttr}>
        <div class="card-body">
          <div class="post-header">
            <div class="post-author">
              <div class="avatar">
                ${username ? username.charAt(0).toUpperCase() : '?'}
              </div>
              <div class="author-info">
                <div class="author-name">
                  <user-mention username="${username}" real-username="${username}" user-id="${this._currentUser?.id}" class="author-link"></user-mention>
                </div>
                <div class="post-time" title="${createdDate.toLocaleString()}">
                  ${timeAgo}
                </div>
                ${isRssPost ? `
                <div class="rss-shared-subtitle">
                  shared from ${escapeHtml(rssBlogName)}${this.isGroupPost() && groupName ? ` in ${escapeHtml(groupName)}` : ''}
                </div>
                ` : ''}
                ${showGroupBadge ? `
                <div class="post-group-context">
                  <a href="/groups/${this.groupId}" class="post-group-link">in ${escapeHtml(groupName)}</a>
                </div>
                ` : ''}
              </div>
            </div>
            <div class="post-actions">
              ${is_owner && isRssPost && rss_feed_id ? `
                <button class="btn btn-sm btn-outline rss-email-toggle-btn"
                        title="Friend emails for ${escapeHtml(rssBlogName)}"
                        data-feed-id="${rss_feed_id}"
                        data-notify="${this._post.notify_friends_email ? '1' : '0'}">
                  ${this._post.notify_friends_email ? '🔔' : '🔕'}
                </button>
              ` : ''}
              ${is_owner ? `
                ${(post_type !== 'poll' || (this._post.total_votes || 0) === 0) ? `
                <button class="btn btn-sm btn-outline edit-btn" title="Edit post">
                  ✏️
                </button>
                ` : ''}
                <button class="btn btn-sm btn-outline delete-btn" title="Delete post">
                  🗑️
                </button>
              ` : `
                <button class="btn btn-sm btn-outline report-btn" title="Report this post" data-post-id="${id}">
                  🚩
                </button>
              `}
            </div>
          </div>
          
          ${prompt_title ? `
            <div class="prompt-context">
              <div class="prompt-indicator">
                <washing-machine-icon size="small" interactive="false"></washing-machine-icon>
                <span class="prompt-label">Writing Prompt:</span>
                <button class="prompt-reuse-btn" 
                        data-prompt-id="${this._post.prompt_id || ''}" 
                        data-prompt-title="${escapeHtml(prompt_title)}"
                        data-prompt-body="${escapeHtml(this._post.prompt_body || '')}"
                        data-prompt-type="${escapeHtml(this._post.prompt_type || 'text')}"
                        data-template-type="${escapeHtml(this._post.prompt_template_type || 'simple')}"
                        title="Create a new post with this prompt">
                  <photocopy-icon size="small" interactive="true"></photocopy-icon>
                  <span>Use This Prompt</span>
                </button>
              </div>
              <div class="prompt-title">${escapeHtml(prompt_title)}</div>
            </div>
          ` : ''}
          
          ${imported && import_source === 'facebook' && import_source_site ? `
            <div class="import-badge">
              <span class="import-icon">📡</span>
              <span class="import-text">Auto-imported from ${escapeHtml(import_source_site)}</span>
              ${import_source_url ? `<a href="${escapeHtml(import_source_url)}" target="_blank" rel="noopener noreferrer" class="import-link">View original</a>` : ''}
            </div>
          ` : ''}
          
          ${title && title !== 'Untitled Post' ? `
            <div class="post-title">
              <h2>${escapeHtml(title)}</h2>
            </div>
          ` : ''}
          
          <div class="post-content">
            ${this.renderPostContent(post_type, content_html, mood_icon, mood_status, embed_platform, embed_id, embed_url, embed_content_type, embed_country, url_title, url_description, url_image, resolved_url, this._post)}
          </div>

          ${isRssPost && import_source_url ? `
            <div class="rss-attribution-cta">
              <a href="${escapeHtml(import_source_url)}" target="_blank" rel="noopener noreferrer ugc" class="btn-cta">
                Read on ${escapeHtml(rssBlogName)} →
              </a>
            </div>
          ` : ''}
          
          <div class="post-footer">
            <div class="post-stats">
              <a href="${permalink}" class="post-permalink" title="View post details">
                Post #${id}
              </a>
            </div>
            <div class="post-actions-footer">
              <button class="btn btn-sm btn-outline comments-toggle-btn" data-post-id="${id}">
                ${commentsToggleLabel}
              </button>
            </div>
          </div>
          
          <div class="post-comments" id="post-comments-${id}"${commentsDisplayStyle}>
            <comments-section post-id="${id}" lazy-load="true"${groupIdAttr}></comments-section>
          </div>
        </div>
      </article>
    `;

    this.attachEventListeners();

    const postContext = this.isGroupPost() ? 'group' : 'friend';

    // Note: Images already have proxy URLs from backend (content_html field)
    // Only process if content_html is missing (fallback for old data)
    if (this._currentUser?.id && !this._post?.content_html) {
      try {
        const postContent = this.querySelector('.post-content');
        if (postContent) {
          const processedHtml = await processImageProxyUrls(
            postContent.innerHTML,
            this._currentUser.id,
            this._post?.id || null,
            postContext
          );
          postContent.innerHTML = processedHtml;
        }
      } catch (error) {
        // If proxy processing fails, continue with original URLs
        window.logger.warn('Failed to process image proxy URLs:', error);
      }
    }

    // Convert video/audio img tags to proper video/audio elements
    this.convertMediaImgTags();

    // Remint expired signed proxy tokens while the SPA stays open
    if (this._currentUser?.id) {
      attachProxyImageRemintHandlers(this, {
        postId: this._post?.id || null,
        postContext
      });
    }

    // Skip moderation/flagging in layout editor previews (read-only slot, no API noise)
    if (!this._isLayoutEditorPreview()) {
      try { await applyImageModeration(this); } catch (_e) { try { applyImageGating(this); } catch (_e2) { /* Ignore gating errors */ } }

      // Detect and handle unresolved Google Drive images
      setTimeout(() => {
        this.detectUnresolvedImages().catch(err => {
          window.logger.warn('Error detecting unresolved images:', err);
        });
      }, 100);
    }

    if (this._expandCommentsWhenReady) {
      this._expandCommentsWhenReady = false;
      this.openComments(true);
    }
  }

  _isLayoutEditorPreview() {
    return Boolean(this.closest('.layout-split-editor-preview'));
  }

  /**
   * Convert img tags that represent videos/audio to proper video/audio elements
   */
  convertMediaImgTags(container = null) {
    const postContent = container || this.querySelector('.post-content');
    if (!postContent) {
      window.logger.debug('convertMediaImgTags: No post-content found');
      return;
    }

    const images = postContent.querySelectorAll('img');
    window.logger.debug('convertMediaImgTags: Found images', {
      count: images.length,
      postId: this._post?.id
    });

    images.forEach((img, index) => {
      // Check if this is actually a video or audio file based on filename
      // Check alt attribute, data-image-url, or src for file extension
      const alt = img.getAttribute('alt') || '';
      const dataImageUrl = img.getAttribute('data-image-url') || '';
      const src = img.getAttribute('src') || '';

      // Extract filename from any of these sources
      const filename = alt || dataImageUrl.split('/').pop() || src.split('/').pop() || '';

      // Check video extensions
      const videoExtensions = ['.mp4', '.webm', '.ogv', '.mov', '.avi', '.mkv'];
      const isVideo = videoExtensions.some(ext => filename.toLowerCase().endsWith(ext));

      // Check audio extensions
      const audioExtensions = ['.mp3', '.wav', '.ogg', '.flac', '.m4a', '.aac'];
      const isAudio = audioExtensions.some(ext => filename.toLowerCase().endsWith(ext));

      if (!isVideo && !isAudio) {
        window.logger.debug(`convertMediaImgTags: Image ${index} is not video/audio`, {
          filename,
          alt,
          src: src.substring(0, 100),
          dataImageUrl: dataImageUrl.substring(0, 100)
        });
        return; // Not a video or audio file
      }

      // Get the source URL - use src (which has the proxy URL) rather than data-image-url (which has oauth://)
      // The src attribute will have the proxy URL after processImageProxyUrls runs
      const sourceUrl = src || dataImageUrl;

      if (!sourceUrl) {
        window.logger.warn('convertMediaImgTags: No source URL found for media', {
          filename,
          isVideo,
          isAudio
        });
        return; // No URL to use
      }

      window.logger.debug('convertMediaImgTags: Converting to media element', {
        index,
        filename,
        isVideo,
        isAudio,
        sourceUrl: sourceUrl.substring(0, 100),
        postId: this._post?.id
      });

      // Create the appropriate element
      let newElement;
      let errorWrapper = null;
      if (isVideo) {
        newElement = document.createElement('video');
        newElement.setAttribute('controls', '');
        newElement.setAttribute('preload', 'metadata');
        newElement.className = 'user-video';
        newElement.style.maxWidth = '100%';
        newElement.style.height = 'auto';
        newElement.style.borderRadius = '8px';

        const source = document.createElement('source');
        source.setAttribute('src', sourceUrl);
        // Try to detect MIME type from extension
        if (filename.toLowerCase().endsWith('.webm')) {
          source.setAttribute('type', 'video/webm');
        } else if (filename.toLowerCase().endsWith('.ogv')) {
          source.setAttribute('type', 'video/ogg');
        } else if (filename.toLowerCase().endsWith('.mov')) {
          source.setAttribute('type', 'video/quicktime');
        } else {
          source.setAttribute('type', 'video/mp4');
        }
        newElement.appendChild(source);

        // Copy over relevant attributes
        if (img.getAttribute('loading')) {
          newElement.setAttribute('loading', img.getAttribute('loading'));
        }
        if (img.getAttribute('referrerpolicy')) {
          newElement.setAttribute('referrerpolicy', img.getAttribute('referrerpolicy'));
        }
        if (img.getAttribute('crossorigin')) {
          newElement.setAttribute('crossorigin', img.getAttribute('crossorigin'));
        }
        if (img.getAttribute('data-image-url')) {
          newElement.setAttribute('data-video-url', img.getAttribute('data-image-url'));
        }


        // Add error display wrapper (create before event listeners)
        errorWrapper = document.createElement('div');
        errorWrapper.className = 'video-error-wrapper';
        errorWrapper.style.display = 'none';
        errorWrapper.innerHTML = `
          <div class="video-error-message">
            <span class="error-icon">⚠️</span>
            <span class="error-text">Video failed to load</span>
            <button class="btn btn-sm btn-secondary retry-video-btn" data-video-src="${escapeHtml(sourceUrl)}">Retry</button>
          </div>
        `;

        // Add error handling and debugging for video elements
        newElement.addEventListener('error', (e) => {
          const video = e.target;
          const error = video.error;
          let errorMessage = 'Video failed to load';

          if (error) {
            switch (error.code) {
            case error.MEDIA_ERR_ABORTED:
              errorMessage = 'Video loading aborted';
              break;
            case error.MEDIA_ERR_NETWORK:
              errorMessage = 'Network error while loading video';
              break;
            case error.MEDIA_ERR_DECODE:
              errorMessage = 'Video decoding error';
              break;
            case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
              errorMessage = 'Video format not supported';
              break;
            }
          }

          window.logger.error('Video element error', {
            errorCode: error?.code,
            errorMessage,
            src: sourceUrl,
            sourceType: source.getAttribute('type'),
            networkState: video.networkState,
            readyState: video.readyState,
            postId: this._post?.id
          });

          // Show error wrapper
          if (errorWrapper) {
            errorWrapper.style.display = 'block';
            newElement.style.display = 'none';
          }

          // Try to fetch the URL to check what's actually being returned
          fetch(sourceUrl, { method: 'HEAD' })
            .then(response => {
              window.logger.debug('Video URL HEAD request', {
                url: sourceUrl,
                status: response.status,
                statusText: response.statusText,
                contentType: response.headers.get('content-type'),
                contentLength: response.headers.get('content-length'),
                xImageError: response.headers.get('x-image-error'),
                xOriginalError: response.headers.get('x-original-error'),
                cacheControl: response.headers.get('cache-control')
              });
            })
            .catch(fetchError => {
              window.logger.error('Failed to fetch video URL for debugging', {
                url: sourceUrl,
                error: fetchError.message
              });
            });
        });

        // Log initial state immediately after creation
        window.logger.debug('Video element created', {
          src: sourceUrl,
          sourceType: source.getAttribute('type'),
          hasControls: newElement.hasAttribute('controls'),
          preload: newElement.getAttribute('preload'),
          className: newElement.className,
          postId: this._post?.id
        });

        newElement.addEventListener('loadstart', () => {
          window.logger.debug('Video load started', {
            src: sourceUrl,
            networkState: newElement.networkState,
            readyState: newElement.readyState,
            postId: this._post?.id
          });
        });

        newElement.addEventListener('loadedmetadata', () => {
          window.logger.debug('Video metadata loaded', {
            src: sourceUrl,
            duration: newElement.duration,
            videoWidth: newElement.videoWidth,
            videoHeight: newElement.videoHeight,
            postId: this._post?.id
          });
        });

        newElement.addEventListener('canplay', () => {
          window.logger.debug('Video can play', {
            src: sourceUrl,
            postId: this._post?.id
          });
        });

        newElement.addEventListener('stalled', () => {
          window.logger.warn('Video loading stalled', {
            src: sourceUrl,
            networkState: newElement.networkState,
            postId: this._post?.id
          });
        });

        // Retry button handler
        const retryBtn = errorWrapper.querySelector('.retry-video-btn');
        if (retryBtn) {
          retryBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            window.logger.debug('Retrying video load', { src: sourceUrl });
            errorWrapper.style.display = 'none';
            newElement.style.display = 'block';
            newElement.load(); // Reload the video
          });
        }
      } else if (isAudio) {
        newElement = document.createElement('audio');
        newElement.setAttribute('controls', '');
        newElement.setAttribute('preload', 'metadata');
        newElement.className = 'user-audio';
        newElement.style.width = '100%';
        newElement.style.maxWidth = '100%';

        const source = document.createElement('source');
        source.setAttribute('src', sourceUrl);
        // Try to detect MIME type from extension
        if (filename.toLowerCase().endsWith('.ogg')) {
          source.setAttribute('type', 'audio/ogg');
        } else if (filename.toLowerCase().endsWith('.wav')) {
          source.setAttribute('type', 'audio/wav');
        } else if (filename.toLowerCase().endsWith('.flac')) {
          source.setAttribute('type', 'audio/flac');
        } else if (filename.toLowerCase().endsWith('.m4a') || filename.toLowerCase().endsWith('.aac')) {
          source.setAttribute('type', 'audio/mp4');
        } else {
          source.setAttribute('type', 'audio/mpeg');
        }
        newElement.appendChild(source);

        // Copy over relevant attributes
        if (img.getAttribute('data-image-url')) {
          newElement.setAttribute('data-audio-url', img.getAttribute('data-image-url'));
        }
      }

      if (newElement) {
        window.logger.debug('convertMediaImgTags: Replacing img with media element', {
          filename,
          elementType: newElement.tagName,
          sourceUrl: sourceUrl.substring(0, 100),
          postId: this._post?.id
        });

        // For videos, wrap in container to include error display
        if (isVideo && errorWrapper) {
          const container = document.createElement('div');
          container.className = 'video-container';
          container.appendChild(newElement);
          container.appendChild(errorWrapper);
          img.parentNode.replaceChild(container, img);
          // Check video element state after it's in the DOM
          setTimeout(() => {
            const videoInDom = container.querySelector('video');
            if (videoInDom) {
              window.logger.debug('convertMediaImgTags: Video element in DOM', {
                filename,
                networkState: videoInDom.networkState,
                readyState: videoInDom.readyState,
                currentSrc: videoInDom.currentSrc?.substring(0, 100),
                error: videoInDom.error ? {
                  code: videoInDom.error.code,
                  message: videoInDom.error.message
                } : null,
                postId: this._post?.id
              });
            } else {
              window.logger.warn('convertMediaImgTags: Video element not found in DOM after creation');
            }
          }, 100);

          window.logger.debug('convertMediaImgTags: Video element created and wrapped', {
            filename,
            videoSrc: newElement.querySelector('source')?.getAttribute('src')?.substring(0, 100),
            hasErrorWrapper: !!errorWrapper,
            postId: this._post?.id
          });
        } else {
          // Replace the img tag with the video/audio element
          img.parentNode.replaceChild(newElement, img);
          window.logger.debug('convertMediaImgTags: Media element created', {
            filename,
            elementType: newElement.tagName,
            postId: this._post?.id
          });
        }
      } else {
        window.logger.warn('convertMediaImgTags: Failed to create media element', {
          filename,
          isVideo,
          isAudio
        });
      }
    });
  }

  /**
   * Detect unresolved Google Drive images/videos/audio and add UI for reimport
   */
  async detectUnresolvedImages() {
    if (!this._post || !this._currentUser) return;

    // Only show reimport UI for Facebook-imported posts owned by the user
    if (this._post.source !== 'facebook' || !this._post.is_owner) {
      window.logger.debug('Skipping unresolved media detection', {
        source: this._post.source,
        is_owner: this._post.is_owner,
        postId: this._post.id
      });
      return;
    }

    const postContent = this.querySelector('.post-content');
    if (!postContent) return;

    /**
     * Helper function to get the URL from a media element
     * Checks appropriate attributes based on element type
     */
    const getMediaUrl = (element) => {
      if (element.tagName === 'IMG') {
        // For images, check data-image-url first, then src
        return element.getAttribute('data-image-url') || element.getAttribute('src') || '';
      } else if (element.tagName === 'VIDEO') {
        // For videos, check data-video-url first, then source src, then video src
        const dataVideoUrl = element.getAttribute('data-video-url') || '';
        if (dataVideoUrl) return dataVideoUrl;
        const source = element.querySelector('source');
        if (source) return source.getAttribute('src') || '';
        return element.getAttribute('src') || '';
      } else if (element.tagName === 'AUDIO') {
        // For audio, check data-audio-url first, then source src, then audio src
        const dataAudioUrl = element.getAttribute('data-audio-url') || '';
        if (dataAudioUrl) return dataAudioUrl;
        const source = element.querySelector('source');
        if (source) return source.getAttribute('src') || '';
        return element.getAttribute('src') || '';
      }
      return '';
    };

    /**
     * Helper function to check if a URL indicates unresolved/error state
     */
    const isUnresolvedUrl = (url) => {
      return url.includes('oauth://google-drive') &&
             (url.includes('unresolved') || url.includes('error'));
    };

    // Find all media elements (images, videos, audio) with unresolved/error URLs
    const allImages = postContent.querySelectorAll('img');
    const allVideos = postContent.querySelectorAll('video');
    const allAudio = postContent.querySelectorAll('audio');

    const unresolvedMedia = [];

    // Check images
    for (const img of allImages) {
      const url = getMediaUrl(img);
      if (isUnresolvedUrl(url)) {
        unresolvedMedia.push({ element: img, url, type: 'image' });
      }
    }

    // Check videos
    for (const video of allVideos) {
      const url = getMediaUrl(video);
      if (isUnresolvedUrl(url)) {
        unresolvedMedia.push({ element: video, url, type: 'video' });
      }
    }

    // Check audio
    for (const audio of allAudio) {
      const url = getMediaUrl(audio);
      if (isUnresolvedUrl(url)) {
        unresolvedMedia.push({ element: audio, url, type: 'audio' });
      }
    }

    if (unresolvedMedia.length === 0) {
      window.logger.debug('No unresolved media found', { postId: this._post.id });
      return;
    }

    window.logger.debug('Found unresolved media', {
      postId: this._post.id,
      count: unresolvedMedia.length
    });

    // Get Google Drive accounts
    let googleDriveAccounts = [];
    try {
      const data = await window.API.request(`/users/settings/${this._currentUser.id}/media-sources`);
      googleDriveAccounts = data.accounts?.['google-drive'] || [];
    } catch (error) {
      window.logger.warn('Failed to load Google Drive accounts for unresolved media reimport', error);
      return;
    }

    if (googleDriveAccounts.length === 0) return;

    // Process each unresolved media element
    for (const { element, url: urlToCheck, type: detectedType } of unresolvedMedia) {

      // Extract original filename from URL
      // Format: oauth://google-drive/{accountIdentifier}/unresolved-{encodedURI} or error-{encodedURI}
      const match = urlToCheck.match(/oauth:\/\/google-drive\/[^/]+\/(?:unresolved|error)-(.+)/);
      if (!match) {
        window.logger.debug('No match for unresolved URL pattern', {
          urlToCheck,
          elementType: element.tagName
        });
        continue;
      }

      try {
        const encodedUri = decodeURIComponent(match[1]);
        const originalFileName = encodedUri.split('/').pop() || encodedUri;

        // Determine media type: use detected type from element, or check file extension
        let mediaType = detectedType;
        let mediaTypeLabel = detectedType.charAt(0).toUpperCase() + detectedType.slice(1);

        // If type wasn't detected from element, check file extension
        if (mediaType === 'image') {
          const videoExtensions = ['.mp4', '.webm', '.ogv', '.mov', '.avi', '.mkv'];
          const audioExtensions = ['.mp3', '.wav', '.ogg', '.flac', '.m4a', '.aac'];
          const isVideo = videoExtensions.some(ext => originalFileName.toLowerCase().endsWith(ext));
          const isAudio = audioExtensions.some(ext => originalFileName.toLowerCase().endsWith(ext));

          if (isVideo) {
            mediaType = 'video';
            mediaTypeLabel = 'Video';
          } else if (isAudio) {
            mediaType = 'audio';
            mediaTypeLabel = 'Audio';
          }
        }

        // Create wrapper for the media with reimport button
        const wrapper = document.createElement('div');
        wrapper.className = `unresolved-${mediaType}-wrapper`;

        // For videos/audio that are already converted, use the existing element
        // For images, use the img tag HTML
        let mediaElement;
        if (mediaType === 'video' && element.tagName === 'VIDEO') {
          // Video is already a video element, clone it and update src
          const videoClone = element.cloneNode(true);
          const source = videoClone.querySelector('source');
          if (source) {
            source.setAttribute('src', urlToCheck);
          } else {
            videoClone.setAttribute('src', urlToCheck);
          }
          videoClone.className = 'unresolved-media';
          mediaElement = videoClone.outerHTML;
        } else if (mediaType === 'audio' && element.tagName === 'AUDIO') {
          // Audio is already an audio element, clone it and update src
          const audioClone = element.cloneNode(true);
          const source = audioClone.querySelector('source');
          if (source) {
            source.setAttribute('src', urlToCheck);
          } else {
            audioClone.setAttribute('src', urlToCheck);
          }
          audioClone.className = 'unresolved-media';
          mediaElement = audioClone.outerHTML;
        } else if (mediaType === 'video') {
          // Image that should be a video - create video element
          mediaElement = `<video class="unresolved-media" controls>
            <source src="${escapeHtml(urlToCheck)}" type="video/mp4">
            Your browser does not support the video tag.
          </video>`;
        } else if (mediaType === 'audio') {
          // Image that should be audio - create audio element
          mediaElement = `<audio class="unresolved-media" controls>
            <source src="${escapeHtml(urlToCheck)}" type="audio/mpeg">
            Your browser does not support the audio tag.
          </audio>`;
        } else {
          // Image - use existing img element HTML
          mediaElement = element.outerHTML;
        }

        wrapper.innerHTML = `
          <div class="unresolved-media-container">
            ${mediaElement}
            <div class="unresolved-media-block">
              <div class="unresolved-media-message">
                <span class="unresolved-icon">⚠️</span>
                <span class="unresolved-text">${mediaTypeLabel} not found</span>
              </div>
              <button class="btn btn-sm btn-primary resolve-media-btn" 
                      data-post-id="${this._post.id}"
                      data-original-filename="${escapeHtml(originalFileName)}"
                      data-original-uri="${escapeHtml(encodedUri)}"
                      data-media-type="${mediaType}"
                      title="Select ${mediaTypeLabel.toLowerCase()} file from Google Drive">
                📁 Select ${mediaTypeLabel} File
              </button>
            </div>
          </div>
        `;

        // Replace the element with the wrapper
        element.parentNode.replaceChild(wrapper, element);

        // Attach click handler
        const resolveBtn = wrapper.querySelector('.resolve-media-btn');
        if (resolveBtn) {
          resolveBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await this.handleResolveImage(e.target, googleDriveAccounts[0], originalFileName);
          });
        }
      } catch (error) {
        window.logger.warn('Error processing unresolved media', {
          error: error.message,
          urlToCheck,
          elementType: element.tagName
        });
      }
    }
  }

  /**
   * Handle resolving an unresolved image by opening Google Drive picker
   */
  async handleResolveImage(button, googleDriveAccount, searchFileName) {
    if (!this._post || !this._currentUser) return;

    button.disabled = true;
    button.textContent = 'Opening picker...';

    try {
      await openGoogleDrivePicker(
        this,
        'google-drive',
        googleDriveAccount.accountIdentifier,
        'DOCS',
        async (data) => {
          if (data.action === google.picker.Action.PICKED) {
            const file = data.docs[0];
            if (!file) return;

            button.disabled = true;
            button.textContent = 'Reimporting...';

            try {
              // Submit reimport job
              const _response = await window.API.request('/facebook-import/reimport-post', {
                method: 'POST',
                body: JSON.stringify({
                  postId: this._post.id,
                  imageFileId: file.id,
                  imageFileName: file.name
                })
              });

              // Show success message
              button.textContent = '✓ Reimporting...';
              button.classList.add('btn-success-flash');

              // Reload post after a delay to show updated content
              setTimeout(async () => {
                await this.loadPost();
              }, 2000);

            } catch (error) {
              window.logger.error('Failed to submit reimport job:', error);
              alert(`Failed to start reimport: ${error.message || 'Please try again.'}`);
              button.disabled = false;
              button.textContent = '📁 Select Image File';
            }
          }
        },
        false, // single select
        searchFileName // pre-populate search with filename
      );
    } catch (error) {
      window.logger.error('Failed to open Google Drive picker:', error);
      alert(`Failed to open file picker: ${error.message || 'Please try again.'}`);
      button.disabled = false;
      button.textContent = '📁 Select Image File';
    }
  }

  /**
   * Load post data (for re-rendering after reimport)
   */
  async loadPost() {
    if (!this._post?.id) return;

    try {
      const post = await window.API.request(`/posts/${this._post.id}`);
      this._post = post;
      await this.render();
    } catch (error) {
      window.logger.error('Failed to reload post:', error);
    }
  }

  attachEventListeners() {
    const editBtn = this.querySelector('.edit-btn');
    const deleteBtn = this.querySelector('.delete-btn');
    const reportBtn = this.querySelector('.report-btn');
    const commentsToggleBtn = this.querySelector('.comments-toggle-btn');
    const promptReuseBtn = this.querySelector('.prompt-reuse-btn');

    editBtn?.addEventListener('click', () => {
      this.handleEdit();
    });

    deleteBtn?.addEventListener('click', () => {
      this.handleDelete();
    });

    reportBtn?.addEventListener('click', () => {
      this.handleReport();
    });

    commentsToggleBtn?.addEventListener('click', () => {
      this.toggleComments();
    });

    promptReuseBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.handlePromptReuse(e.target.closest('.prompt-reuse-btn'));
    });

    const rssEmailBtn = this.querySelector('.rss-email-toggle-btn');
    rssEmailBtn?.addEventListener('click', () => this.handleRssEmailToggle(rssEmailBtn));
  }

  async handleRssEmailToggle(btn) {
    const feedId = parseInt(btn.dataset.feedId, 10);
    const currentlyOn = btn.dataset.notify === '1';
    try {
      await window.API.rss.update(feedId, { notify_friends_email: !currentlyOn });
      if (this._post) {
        this._post.notify_friends_email = !currentlyOn;
      }
      btn.dataset.notify = currentlyOn ? '0' : '1';
      btn.textContent = currentlyOn ? '🔕' : '🔔';
    } catch (error) {
      window.logger.error('Failed to toggle RSS email notifications:', error);
      this.showError('Could not update email setting for this feed');
    }
  }

  async handleReport() {
    const postId = this._post.id;
    if (!postId) {
      window.logger.error('Cannot report post: no post ID');
      return;
    }

    // Show report modal
    const reason = prompt('Why are you reporting this post?\n\nPlease provide a brief reason (e.g., "Inappropriate content", "Spam", "Harassment", etc.):');

    if (!reason || reason.trim().length === 0) {
      return; // User cancelled or didn't provide reason
    }

    const description = prompt('Additional details (optional):\n\nProvide any additional context that might help us understand the issue:') || '';

    try {
      if (this.isGroupPost()) {
        await window.API.request(`/groups/${this.groupId}/reports`, {
          method: 'POST',
          body: JSON.stringify({
            target_type: 'post',
            target_id: postId,
            reason: reason.trim(),
            details: description.trim()
          })
        });
        alert('Thank you for reporting this post. Group moderators will review it.');
        return;
      }

      const response = await window.API.request('/support/flag-post', {
        method: 'POST',
        body: JSON.stringify({
          post_id: postId,
          reason: reason.trim(),
          description: description.trim()
        })
      });

      alert('Thank you for reporting this post. Our team will review it shortly.');

      // Optionally open the safety armadillo to show the created ticket
      const safetyArmadillo = document.querySelector('safety-armadillo');
      if (safetyArmadillo && response.ticket) {
        safetyArmadillo.open();
        setTimeout(() => {
          safetyArmadillo.viewTicket(response.ticket.id || response.ticket.conversation_id);
        }, 500);
      }
    } catch (error) {
      window.logger.error('Failed to report post:', error);
      alert(`Failed to report post: ${error.message || 'Please try again later.'}`);
    }
  }

  handleEdit() {
    if (this._post.post_type === 'poll') {
      this.handleEditPoll();
      return;
    }
    const postTextElement = this.querySelector('.post-text');
    const postTitleElement = this.querySelector('.post-title');
    if (!postTextElement) {
      window.logger?.warn('handleEdit: no .post-text element (post_type may not support editing)');
      alert('Editing is not supported for this post type.');
      return;
    }
    const currentContent = this._post.content || this._post.content_markdown || '';
    const currentTitle = this._post.title || '';
    const currentStyle = this._post.post_style || null;
    const showStylePicker = !this.isGroupPost();
    const styleField = showStylePicker
      ? '<post-style-picker class="edit-style-picker"></post-style-picker>'
      : '';

    // Create edit form
    const editForm = document.createElement('div');
    editForm.className = 'edit-form';
    const titleField = this.isGroupPost() ? '' : `
      <input type="text" class="edit-title-input" placeholder="Post title..." maxlength="255" value="${escapeHtml(currentTitle)}">
    `;
    editForm.innerHTML = `
      ${titleField}
      <markdown-preview>
          <textarea class="edit-textarea" rows="4">${escapeHtml(currentContent)}</textarea>
      </markdown-preview>
      ${styleField}
      <div class="edit-actions">
        <button type="button" class="btn btn-sm btn-primary save-edit-btn">Save</button>
        <button type="button" class="btn btn-sm btn-secondary cancel-edit-btn">Cancel</button>
      </div>
    `;

    // Replace post content with edit form
    postTextElement.style.display = 'none';
    if (postTitleElement) {
      postTitleElement.style.display = 'none';
    }
    postTextElement.parentNode.insertBefore(editForm, postTextElement.nextSibling);

    const stylePicker = showStylePicker ? editForm.querySelector('post-style-picker') : null;
    const markdownPreview = editForm.querySelector('markdown-preview');
    if (stylePicker) {
      stylePicker.setStyle(currentStyle);

      const applyEditStylePreview = (style, flipPreview = false) => {
        if (!markdownPreview) return;
        if (flipPreview) {
          markdownPreview.setMode('preview');
        }
        if (style) {
          markdownPreview.applyPostStyle(style);
        } else {
          markdownPreview.clearPostStyle();
        }
      };

      editForm.addEventListener('post-style-preview-request', () => {
        applyEditStylePreview(stylePicker.getStyle(), true);
      });
      editForm.addEventListener('post-style-change', (e) => {
        applyEditStylePreview(e.detail?.style ?? null);
      });
    }

    // Focus on title input and select all text
    const titleInput = editForm.querySelector('.edit-title-input');
    const textarea = editForm.querySelector('.edit-textarea');
    if (markdownPreview?.bindTitleInput && titleInput) {
      markdownPreview.bindTitleInput(titleInput);
    }
    if (titleInput) {
      titleInput.focus();
      titleInput.setSelectionRange(0, titleInput.value.length);
    } else {
      textarea.focus();
      textarea.setSelectionRange(0, textarea.value.length);
    }

    // Hide edit/delete buttons during editing
    const postActions = this.querySelector('.post-actions');
    if (postActions) {
      postActions.style.display = 'none';
    }

    // Handle save
    editForm.querySelector('.save-edit-btn').addEventListener('click', async () => {
      const newTitle = titleInput ? titleInput.value.trim() : '';
      const newContent = textarea.value.trim();

      if (!newContent) {
        alert('Post content cannot be empty');
        return;
      }

      const newStyle = stylePicker ? stylePicker.getStyle() : null;
      const stylesEqual = JSON.stringify(currentStyle || null) === JSON.stringify(newStyle || null);

      if (newContent === currentContent && newTitle === currentTitle && stylesEqual) {
        // No changes, just cancel
        this.cancelEdit(editForm, postTextElement, postTitleElement, postActions);
        return;
      }

      try {
        // Disable buttons during save
        const saveBtn = editForm.querySelector('.save-edit-btn');
        const cancelBtn = editForm.querySelector('.cancel-edit-btn');
        saveBtn.disabled = true;
        cancelBtn.disabled = true;
        saveBtn.textContent = 'Saving...';

        let updatedPost;
        if (this.isGroupPost()) {
          updatedPost = await window.API.request(
            `/groups/${this.groupId}/posts/${this._post.id}`,
            {
              method: 'PUT',
              body: JSON.stringify({ content_markdown: newContent })
            }
          );
        } else {
          const updatePayload = {
            title: newTitle,
            content: newContent
          };
          if (stylePicker) {
            updatePayload.postStyle = newStyle;
            const lastUsedStyleId = stylePicker.getLastUsedStyleId?.();
            if (lastUsedStyleId) {
              updatePayload.lastUsedStyleId = lastUsedStyleId;
            }
          }
          updatedPost = await window.API.posts.update(this._post.id, updatePayload);
        }

        // Update the post data and re-render
        this._post = { ...this._post, ...updatedPost };
        this.render();

        // Dispatch custom event to notify parent components
        this.dispatchEvent(new CustomEvent('post-updated', {
          detail: { postId: this._post.id, updatedPost: this._post },
          bubbles: true
        }));

      } catch (error) {
        window.logger.error('Failed to update post:', error);
        const message = error.message || 'Failed to update post. Please try again.';
        if (stylePicker?.showError) {
          stylePicker.showError(message);
        } else {
          alert(message);
        }

        // Re-enable buttons
        const saveBtn = editForm.querySelector('.save-edit-btn');
        const cancelBtn = editForm.querySelector('.cancel-edit-btn');
        saveBtn.disabled = false;
        cancelBtn.disabled = false;
        saveBtn.textContent = 'Save';
      }
    });

    // Handle cancel
    editForm.querySelector('.cancel-edit-btn').addEventListener('click', () => {
      this.cancelEdit(editForm, postTextElement, postTitleElement, postActions);
    });

    // Handle Escape key to cancel
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.cancelEdit(editForm, postTextElement, postTitleElement, postActions);
      }
    });
  }

  cancelEdit(editForm, postTextElement, postTitleElement, postActions) {
    editForm.remove();
    postTextElement.style.display = 'block';
    if (postTitleElement) {
      postTitleElement.style.display = 'block';
    }
    if (postActions) {
      postActions.style.display = 'flex';
    }
  }

  handleEditPoll() {
    const postContent = this.querySelector('.post-content');
    const postTitle = this.querySelector('.post-title');
    const postActions = this.querySelector('.post-actions');
    if (!postContent) return;

    const question = this._post.poll_question || '';
    const options = (this._post.poll_options || []).map(o =>
      typeof o === 'string' ? o : (o.option_text || '')
    );
    const optionsHtml = options.map((opt, i) => `
      <div class="poll-option-item" data-index="${i}">
        <input type="text" class="poll-option-input form-control" data-index="${i}"
          placeholder="Option ${i + 1}" value="${escapeHtml(opt)}" maxlength="500" />
        <button type="button" class="btn btn-icon remove-option-btn" data-index="${i}" title="Remove option"
          style="${options.length <= 2 ? 'display:none' : ''}">✕</button>
      </div>
    `).join('');

    if (postTitle) postTitle.style.display = 'none';
    if (postActions) postActions.style.display = 'none';

    postContent.innerHTML = `
      <div class="edit-form poll-edit-form">
        <div class="form-group">
          <label for="poll-edit-question">Poll Question</label>
          <textarea id="poll-edit-question" class="form-control poll-question-input" rows="2"
            maxlength="500" placeholder="What's your question?">${escapeHtml(question)}</textarea>
          <div class="char-count"><span id="poll-edit-char-count">${question.length}</span>/500</div>
        </div>
        <div class="form-group">
          <label>Poll Options <span class="option-count">(${options.length}/10)</span></label>
          <div class="poll-options-list" id="poll-edit-options"></div>
          ${options.length < 10 ? '<button type="button" class="btn btn-secondary add-option-btn" id="poll-edit-add">+ Add Option</button>' : ''}
        </div>
        <div class="edit-actions">
          <button type="button" class="btn btn-sm btn-primary save-edit-btn">Save</button>
          <button type="button" class="btn btn-sm btn-secondary cancel-edit-btn">Cancel</button>
        </div>
      </div>
    `;

    const list = postContent.querySelector('#poll-edit-options');
    list.innerHTML = optionsHtml;

    const questionInput = postContent.querySelector('#poll-edit-question');
    const charCount = postContent.querySelector('#poll-edit-char-count');
    const optionCountLabel = postContent.querySelector('.option-count');
    const addBtn = postContent.querySelector('#poll-edit-add');

    const getOptionInputs = () => postContent.querySelectorAll('.poll-option-input');
    const getOptionCount = () => getOptionInputs().length;

    const updateOptionCount = () => {
      const n = getOptionCount();
      if (optionCountLabel) optionCountLabel.textContent = `(${n}/10)`;
      const addEl = postContent.querySelector('#poll-edit-add');
      if (addEl) addEl.style.display = n >= 10 ? 'none' : 'inline-block';
    };

    questionInput.addEventListener('input', () => {
      if (charCount) charCount.textContent = questionInput.value.length;
    });

    const addOption = () => {
      if (getOptionCount() >= 10) return;
      const idx = getOptionCount();
      const item = document.createElement('div');
      item.className = 'poll-option-item';
      item.dataset.index = String(idx);
      const showRemove = getOptionCount() >= 2;
      item.innerHTML = `
        <input type="text" class="poll-option-input form-control" data-index="${idx}"
          placeholder="Option ${idx + 1}" maxlength="500" />
        <button type="button" class="btn btn-icon remove-option-btn" data-index="${idx}" title="Remove option"
          style="${showRemove ? '' : 'display:none'}">✕</button>
      `;
      list.appendChild(item);
      item.querySelector('.poll-option-input').focus();
      reindexOptions();
      updateOptionCount();
      attachOptionListeners();
    };

    const removeOption = (index) => {
      const items = list.querySelectorAll('.poll-option-item');
      if (items.length <= 2) return;
      items[index]?.remove();
      reindexOptions();
      updateOptionCount();
      attachOptionListeners();
    };

    const reindexOptions = () => {
      const items = list.querySelectorAll('.poll-option-item');
      items.forEach((item, i) => {
        item.dataset.index = String(i);
        const input = item.querySelector('.poll-option-input');
        const removeBtn = item.querySelector('.remove-option-btn');
        if (input) {
          input.dataset.index = String(i);
          input.placeholder = `Option ${i + 1}`;
        }
        if (removeBtn) {
          removeBtn.dataset.index = String(i);
          removeBtn.style.display = items.length > 2 ? '' : 'none';
        }
      });
    };

    const attachOptionListeners = () => {
      list.querySelectorAll('.remove-option-btn').forEach(btn => {
        btn.replaceWith(btn.cloneNode(true));
      });
      list.querySelectorAll('.remove-option-btn').forEach(btn => {
        btn.addEventListener('click', () => removeOption(parseInt(btn.dataset.index, 10)));
      });
    };
    attachOptionListeners();

    if (addBtn) addBtn.addEventListener('click', addOption);

    postContent.querySelector('.cancel-edit-btn').addEventListener('click', () => {
      this.render().catch(e => window.logger?.error('Poll edit cancel render', e));
    });

    postContent.querySelector('.save-edit-btn').addEventListener('click', async () => {
      const trimmedQuestion = questionInput.value.trim();
      if (!trimmedQuestion) {
        alert('Please enter a poll question.');
        return;
      }
      const rawOptions = Array.from(getOptionInputs()).map(inp => inp.value.trim()).filter(Boolean);
      if (rawOptions.length < 2) {
        alert('Polls must have at least 2 options.');
        return;
      }
      if (rawOptions.length > 10) {
        alert('Polls can have at most 10 options.');
        return;
      }
      const seen = new Set();
      for (const o of rawOptions) {
        const lower = o.toLowerCase();
        if (seen.has(lower)) {
          alert('Poll options must be unique (case-insensitive).');
          return;
        }
        seen.add(lower);
      }

      const saveBtn = postContent.querySelector('.save-edit-btn');
      const cancelBtn = postContent.querySelector('.cancel-edit-btn');
      saveBtn.disabled = true;
      cancelBtn.disabled = true;
      saveBtn.textContent = 'Saving...';

      try {
        const updated = await window.API.polls.update(this._post.id, {
          poll_question: trimmedQuestion,
          poll_options: rawOptions
        });
        this._post = { ...this._post, ...updated };
        await this.render();
        this.dispatchEvent(new CustomEvent('post-updated', {
          detail: { postId: this._post.id, updatedPost: this._post },
          bubbles: true
        }));
      } catch (err) {
        window.logger?.error('Failed to update poll', err);
        const msg = err.message || err.error || (err.details && err.details.map(d => d.message).join(', ')) || 'Failed to update poll. Please try again.';
        alert(msg);
        saveBtn.disabled = false;
        cancelBtn.disabled = false;
        saveBtn.textContent = 'Save';
      }
    });

    questionInput.focus();
  }

  async handleDelete() {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      if (this.isGroupPost()) {
        await window.API.request(`/groups/${this.groupId}/posts/${this._post.id}`, {
          method: 'DELETE'
        });
      } else {
        await window.API.posts.delete(this._post.id);
      }

      // Dispatch custom event to notify parent components
      this.dispatchEvent(new CustomEvent('post-deleted', {
        detail: { postId: this._post.id },
        bubbles: true
      }));

      // Remove the element with animation
      this.style.transition = 'all 0.3s ease-out';
      this.style.transform = 'translateX(-100%)';
      this.style.opacity = '0';

      setTimeout(() => {
        this.remove();
      }, 300);

    } catch (error) {
      window.logger.error('Failed to delete post:', error);
      alert('Failed to delete post. Please try again.');
    }
  }

  openComments(force = false) {
    const commentsContainer = this.querySelector('.post-comments');
    const toggleBtn = this.querySelector('.comments-toggle-btn');

    if (!commentsContainer || !toggleBtn) {
      return;
    }

    const isHidden = commentsContainer.style.display === 'none'
      || window.getComputedStyle(commentsContainer).display === 'none';
    if (!force && !isHidden) {
      return;
    }

    commentsContainer.style.display = 'block';
    toggleBtn.innerHTML = '💬 <span class="comments-count">Hide Comments</span>';
    this._keepCommentsOpen = true;
    this.setAttribute('data-comments-open', 'true');

    const commentsSection = commentsContainer.querySelector('comments-section');
    if (commentsSection && !commentsSection.commentsLoaded) {
      commentsSection.loadCommentsLazy();
    }
  }

  toggleComments() {
    const commentsContainer = this.querySelector('.post-comments');
    const toggleBtn = this.querySelector('.comments-toggle-btn');

    if (commentsContainer && toggleBtn) {
      const isVisible = commentsContainer.style.display !== 'none';
      if (isVisible) {
        commentsContainer.style.display = 'none';
        toggleBtn.innerHTML = `💬 <span class="comments-count">Comments (${this._post.comment_count || 0})</span>`;
        this._keepCommentsOpen = false;
        this.removeAttribute('data-comments-open');
      } else {
        this.openComments();
      }
    }
  }

  handlePromptReuse(btn) {
    if (!btn) return;

    const promptId = btn.dataset.promptId;
    const promptTitle = btn.dataset.promptTitle;
    const promptBody = btn.dataset.promptBody;

    // Trigger photocopy animation
    const photocopyIcon = btn.querySelector('photocopy-icon');
    if (photocopyIcon && photocopyIcon.triggerCopyAnimation) {
      photocopyIcon.triggerCopyAnimation();
    }

    // Navigate to home and set the prompt in the post writer
    if (window.router) {
      window.router.navigate('/');
    }

    // Dispatch event to set the prompt in the post writer
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('prompt-selected', {
        detail: {
          id: promptId,
          title: promptTitle,
          body: promptBody,
          prompt_type: btn.dataset.promptType || 'text',
          template_type: btn.dataset.templateType || 'simple'
        }
      }));
    }, 100);

    // Show feedback
    this.showPromptReuseSuccess(promptTitle);
  }

  showPromptReuseSuccess(promptTitle) {
    // Create temporary success notification
    const notification = document.createElement('div');
    notification.className = 'prompt-reuse-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <photocopy-icon size="small"></photocopy-icon>
        <span>Prompt copied! Ready to write with "${promptTitle}"</span>
      </div>
    `;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--success-color);
      color: white;
      padding: 0.75rem 1rem;
      border-radius: 6px;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease;
      max-width: 300px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  formatTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    }

    // For older posts, show the actual date
    return date.toLocaleDateString();
  }

  renderPostContent(postType, contentHtml, moodIcon, moodStatus, embedPlatform, embedId, embedUrl, embedContentType, embedCountry, urlTitle, urlDescription, urlImage, resolvedUrl, postData) {
    switch (postType) {
    case 'mood':
      return this.renderMoodPost(moodIcon, moodStatus, contentHtml);
    case 'music':
      return this.renderMusicPost(embedPlatform, embedId, embedUrl, embedContentType, embedCountry, urlTitle, urlDescription, urlImage, contentHtml);
    case 'poll':
      return this.renderPollPost(postData || this._post);
    default:
      return this.renderTextPost(
        contentHtml,
        embedPlatform,
        embedId,
        embedUrl,
        embedContentType,
        embedCountry,
        urlTitle,
        urlDescription,
        urlImage,
        resolvedUrl,
        postData?.source === 'rss',
        postData?.prompt_template_type
      );
    }
  }

  renderPollPost(postData) {
    // Prepare poll data for view-poll component
    const pollData = {
      id: postData.id,
      poll_question: postData.poll_question || '',
      poll_options: postData.poll_options || [],
      poll_type: postData.poll_type || 'single',
      poll_closed: postData.poll_closed || false,
      poll_closed_at: postData.poll_closed_at || null,
      total_votes: postData.total_votes,
      total_yes_votes: postData.total_yes_votes,
      total_no_votes: postData.total_no_votes,
      user_voted: postData.user_voted || false,
      can_see_results: postData.can_see_results || false,
      is_owner: postData.is_owner || false
    };

    // Escape JSON for HTML attribute
    const pollDataJson = JSON.stringify(pollData).replace(/"/g, '&quot;');
    const currentUserJson = this._currentUser ? JSON.stringify(this._currentUser).replace(/"/g, '&quot;') : '';

    return `
      <view-poll 
        poll-data="${pollDataJson}"
        ${currentUserJson ? `current-user="${currentUserJson}"` : ''}
      ></view-poll>
    `;
  }

  renderMoodPost(moodIcon, moodStatus, contentHtml) {
    return `
      <div class="mood-post">
        <div class="mood-display">
          <div class="mood-icon-large">${moodIcon}</div>
          ${moodStatus ? `
            <div class="mood-status">
              ${escapeHtml(moodStatus)}
            </div>
          ` : ''}
        </div>
        ${contentHtml ? `
          <div class="post-text">
            ${contentHtml}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderMusicPost(embedPlatform, embedId, embedUrl, embedContentType, embedCountry, urlTitle, urlDescription, urlImage, contentHtml) {
    return `
      <div class="music-post">
        ${embedPlatform && embedId ? `
          <music-player 
            platform="${escapeHtml(embedPlatform)}"
            embed-id="${escapeHtml(embedId)}"
            embed-url="${escapeHtml(embedUrl || '')}"
            content-type="${escapeHtml(embedContentType || '')}"
            country="${escapeHtml(embedCountry || '')}"
            title="${escapeHtml(urlTitle || '')}"
            image="${escapeHtml(urlImage || '')}">
          </music-player>
        ` : `
          <div class="music-fallback">
            <p>🎵 Music link: <a href="${escapeHtml(embedUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(urlTitle || embedUrl)}</a></p>
          </div>
        `}
        ${contentHtml ? `
          <div class="post-text">
            ${contentHtml}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderTextPost(contentHtml, embedPlatform, embedId, embedUrl, embedContentType, embedCountry, urlTitle, urlDescription, urlImage, resolvedUrl, isRssImport = false, promptTemplateType = 'simple') {
    const postContent = contentHtml || '';
    const isStructuredTemplate = promptTemplateType && promptTemplateType !== 'simple';
    const templateClass = isStructuredTemplate ? ' post-text-template' : '';
    let html = `<div class="post-text${templateClass}">${postContent}</div>`;

    if (embedPlatform && embedId) {
      // Show embedded music player for music URLs
      html += `
        <div class="url-preview">
          <music-player 
            platform="${escapeHtml(embedPlatform)}"
            embed-id="${escapeHtml(embedId)}"
            embed-url="${escapeHtml(embedUrl || '')}"
            content-type="${escapeHtml(embedContentType || '')}"
            country="${escapeHtml(embedCountry || '')}"
            title="${escapeHtml(urlTitle || '')}"
            image="${escapeHtml(urlImage || '')}">
          </music-player>
        </div>
      `;
    } else if (urlTitle && resolvedUrl && (!isRssImport || urlImage)) {
      html += `
        <div class="url-preview">
          <div class="url-preview-card">
            ${urlImage ? `<img src="${escapeHtml(urlImage)}" alt="Preview" class="url-preview-image" />` : ''}
            <div class="url-preview-content">
              <h4 class="url-preview-title">
                <a href="${escapeHtml(resolvedUrl)}" target="_blank" rel="noopener noreferrer">
                  ${escapeHtml(urlTitle)}
                </a>
              </h4>
              ${urlDescription ? `<p class="url-preview-description">${escapeHtml(urlDescription)}</p>` : ''}
            </div>
          </div>
        </div>
      `;
    } else if (isRssImport && urlTitle && !urlImage) {
      html += `
        <div class="rss-preview-title-only">
          <h4>${escapeHtml(urlTitle)}</h4>
        </div>
      `;
    }

    return html;
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '20px';
    errorDiv.style.right = '20px';
    errorDiv.style.zIndex = '10000';
    errorDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(errorDiv);

    setTimeout(() => errorDiv.remove(), 4000);
  }
}

// Register the custom element
customElements.define('post-item', PostItem);
